import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@nexus/ui";
import type { ChangelogRoundupCadence } from "@/features/changelog/api/changelog.api";
import { NumberField, SettingRow, SettingSwitch, SettingsCard } from "../shared/SettingsCard";
import { CADENCES, LIMITS } from "./changelog-settings-limits";
import type { ChangelogSettingsCardProps } from "./types";

/**
 * The curated summary that goes to the mailing list: whether it drafts itself,
 * who approves it, and where an unattended one is sent.
 */
export function RoundupsCard({ draft, set, isReadOnly }: ChangelogSettingsCardProps) {
  const { t } = useTranslation();
  const autoSends = !draft.requireRoundupApproval;

  return (
    <SettingsCard
      icon="mail"
      title={t("admin.changelog.settings.roundups.title", "Roundups")}
      description={t(
        "admin.changelog.settings.roundups.description",
        "The curated summary that goes to the mailing list. Whether it drafts itself, who approves it, and where an unattended one is sent.",
      )}
    >
      <SettingRow
        htmlFor="require-roundup-approval"
        label={t(
          "admin.changelog.settings.requireRoundupApproval",
          "Approve each roundup before it sends",
        )}
        description={t(
          "admin.changelog.settings.requireRoundupApprovalHelp",
          "Roundups reach real inboxes, so this stays on unless somebody is watching them.",
        )}
        control={
          <SettingSwitch
            id="require-roundup-approval"
            checked={draft.requireRoundupApproval}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("requireRoundupApproval", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="roundup-auto-send-delay"
        isNested
        isMuted={!autoSends}
        label={t("admin.changelog.settings.roundupAutoSendDelay", "Hold an unattended roundup for")}
        description={t(
          "admin.changelog.settings.roundupAutoSendDelayHelp",
          "A last chance to catch it on the Roundups tab before it reaches inboxes. Zero sends straight away.",
        )}
        control={
          <NumberField
            id="roundup-auto-send-delay"
            value={draft.roundupAutoSendDelayMinutes}
            min={LIMITS.roundupAutoSendDelayMinutes.min}
            max={LIMITS.roundupAutoSendDelayMinutes.max}
            unit={t("admin.changelog.settings.minutes", "minutes")}
            disabled={isReadOnly || !autoSends}
            onChange={(value) => set("roundupAutoSendDelayMinutes", value)}
          />
        }
      />

      <SettingRow
        htmlFor="roundup-notify-email"
        isNested
        isMuted={!autoSends}
        label={t("admin.changelog.settings.roundupNotifyEmail", "Unattended roundups go to email")}
        description={t(
          "admin.changelog.settings.roundupNotifyEmailHelp",
          "Everyone active on the changelog mailing list.",
        )}
        control={
          <SettingSwitch
            id="roundup-notify-email"
            checked={draft.roundupNotifyEmail}
            disabled={isReadOnly || !autoSends}
            onCheckedChange={(checked) => set("roundupNotifyEmail", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="roundup-notify-slack"
        isNested
        isMuted={!autoSends}
        label={t("admin.changelog.settings.roundupNotifySlack", "Unattended roundups go to Slack")}
        description={t(
          "admin.changelog.settings.roundupNotifySlackHelp",
          "The same channel release notes post to.",
        )}
        control={
          <SettingSwitch
            id="roundup-notify-slack"
            checked={draft.roundupNotifySlack}
            disabled={isReadOnly || !autoSends}
            onCheckedChange={(checked) => set("roundupNotifySlack", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="auto-compose-roundups"
        label={t("admin.changelog.settings.autoCompose", "Draft roundups automatically")}
        description={t(
          "admin.changelog.settings.autoComposeHelp",
          "When the cadence window closes, the releases since the last roundup are merged into a draft on their own. With approval on, the draft just waits for you.",
        )}
        control={
          <SettingSwitch
            id="auto-compose-roundups"
            checked={draft.autoComposeRoundups}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("autoComposeRoundups", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="min-deploys-to-compose"
        isNested
        isMuted={!draft.autoComposeRoundups}
        label={t("admin.changelog.settings.minDeploysToCompose", "Draft only when at least")}
        description={t(
          "admin.changelog.settings.minDeploysToComposeHelp",
          "A quiet week makes no roundup — the releases roll into the next one instead.",
        )}
        control={
          <NumberField
            id="min-deploys-to-compose"
            value={draft.minDeploysToAutoCompose}
            min={LIMITS.minDeploysToAutoCompose.min}
            max={LIMITS.minDeploysToAutoCompose.max}
            unit={t("admin.changelog.settings.deploysShipped", "releases shipped")}
            disabled={isReadOnly || !draft.autoComposeRoundups}
            onChange={(value) => set("minDeploysToAutoCompose", value)}
          />
        }
      />

      <SettingRow
        label={t("admin.changelog.settings.roundupCadence", "Roundup cadence")}
        description={t(
          "admin.changelog.settings.roundupCadenceHelp",
          "How often a roundup is due. Sets the window the compose list is prefilled with, and when an automatic draft is started.",
        )}
        control={
          <Select
            value={draft.roundupCadence}
            disabled={isReadOnly}
            onValueChange={(value) => set("roundupCadence", value as ChangelogRoundupCadence)}
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CADENCES.map((cadence) => (
                <SelectItem key={cadence} value={cadence} className="text-xs">
                  {t(`admin.changelog.settings.cadenceOption.${cadence}`, cadence)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
    </SettingsCard>
  );
}
